import * as THREE from "three";
import { pixelsToMeters } from "./export3DGeometry";

const MM_TO_METER = 0.001;

export const createIsoscelesTriangleGeometry = (
  points: number[],
  depthMm: number,
): THREE.BufferGeometry => {
  const d = depthMm * MM_TO_METER;
  const backZ = 0;
  const frontZ = d;

  const vertices2D: Array<[number, number]> = [];
  for (let i = 0; i < 6; i += 2) {
    vertices2D.push([
      pixelsToMeters(points[i]),
      -pixelsToMeters(points[i + 1]),
    ]);
  }

  const geometry = new THREE.BufferGeometry();
  const positions: number[] = [];
  const uvs: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];

  const minX = Math.min(...vertices2D.map((v) => v[0]));
  const maxX = Math.max(...vertices2D.map((v) => v[0]));
  const minY = Math.min(...vertices2D.map((v) => v[1]));
  const maxY = Math.max(...vertices2D.map((v) => v[1]));
  const uvWidth = maxX - minX || 1;
  const uvHeight = maxY - minY || 1;
  const getUV = (x: number, y: number): [number, number] => [
    (x - minX) / uvWidth,
    (y - minY) / uvHeight,
  ];

  for (const [x, y] of vertices2D) {
    positions.push(x, y, frontZ);
    uvs.push(...getUV(x, y));
    normals.push(0, 0, 1);
  }
  indices.push(0, 2, 1);

  for (const [x, y] of vertices2D) {
    positions.push(x, y, backZ);
    uvs.push(...getUV(x, y));
    normals.push(0, 0, -1);
  }
  indices.push(3, 4, 5);

  let sideOffset = 6;
  for (let i = 0; i < 3; i++) {
    const [ax, ay] = vertices2D[i];
    const [bx, by] = vertices2D[(i + 1) % 3];
    const dx = bx - ax,
      dy = by - ay;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = dy / len,
      ny = -dx / len;

    positions.push(
      ax,
      ay,
      frontZ,
      ax,
      ay,
      backZ,
      bx,
      by,
      backZ,
      bx,
      by,
      frontZ,
    );
    normals.push(nx, ny, 0, nx, ny, 0, nx, ny, 0, nx, ny, 0);
    uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);
    indices.push(
      sideOffset,
      sideOffset + 1,
      sideOffset + 2,
      sideOffset,
      sideOffset + 2,
      sideOffset + 3,
    );
    sideOffset += 4;
  }

  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geometry.setIndex(indices);

  return geometry;
};

/**
 * @param flipV - When true, flips V coordinates to compensate for
 *   fillPatternScale.y === -1 (Shopify CDN images with different orientation)
 */
export const createRectTriangleGeometry = (
  width: number,
  height: number,
  depthMm: number,
  flipV = false,
): THREE.BufferGeometry => {
  const w = pixelsToMeters(width);
  const h = pixelsToMeters(height);
  const d = depthMm * MM_TO_METER;
  const backZ = 0;
  const frontZ = d;

  const geometry = new THREE.BufferGeometry();
  const positions: number[] = [];
  const uvs: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];

  // V0: (-w/2, h/2) upper-left, V1: (w/2, h/2) upper-right, V2: (w/2, -h/2) lower-right
  const vertices2D: Array<[number, number]> = [
    [-w / 2, h / 2],
    [w / 2, h / 2],
    [w / 2, -h / 2],
  ];

  const vertexUVs: Array<[number, number]> = flipV
    ? [
        [1, 1],
        [0, 1],
        [0, 0],
      ]
    : [
        [1, 0],
        [0, 0],
        [0, 1],
      ];

  for (let i = 0; i < 3; i++) {
    positions.push(vertices2D[i][0], vertices2D[i][1], frontZ);
    uvs.push(...vertexUVs[i]);
    normals.push(0, 0, 1);
  }
  indices.push(0, 2, 1);

  for (let i = 0; i < 3; i++) {
    positions.push(vertices2D[i][0], vertices2D[i][1], backZ);
    uvs.push(...vertexUVs[i]);
    normals.push(0, 0, -1);
  }
  indices.push(3, 4, 5);

  const sideNormals: Array<[number, number]> = [
    [0, 1],
    [1, 0],
    (() => {
      const len = Math.sqrt(w * w + h * h);
      return [-h / len, -w / len] as [number, number];
    })(),
  ];

  let sideOffset = 6;
  for (let i = 0; i < 3; i++) {
    const [ax, ay] = vertices2D[i];
    const [bx, by] = vertices2D[(i + 1) % 3];
    const [nx, ny] = sideNormals[i];

    positions.push(
      ax,
      ay,
      frontZ,
      ax,
      ay,
      backZ,
      bx,
      by,
      backZ,
      bx,
      by,
      frontZ,
    );
    normals.push(nx, ny, 0, nx, ny, 0, nx, ny, 0, nx, ny, 0);
    uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);
    indices.push(
      sideOffset,
      sideOffset + 1,
      sideOffset + 2,
      sideOffset,
      sideOffset + 2,
      sideOffset + 3,
    );
    sideOffset += 4;
  }

  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geometry.setIndex(indices);

  return geometry;
};

export const createArcGeometry = (
  outerRadius: number,
  angle: number,
  depthMm: number,
): THREE.BufferGeometry => {
  const r = pixelsToMeters(outerRadius);
  const d = depthMm * MM_TO_METER;
  const backZ = 0;
  const frontZ = d;
  const angleRad = (angle * Math.PI) / 180;

  const segments = Math.max(16, Math.ceil(angle / 3));

  const geometry = new THREE.BufferGeometry();
  const positions: number[] = [];
  const uvs: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];

  const startAngle = 0;
  const endAngle = -angleRad;

  const arcVertices: Array<[number, number]> = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const a = startAngle + (endAngle - startAngle) * t;
    arcVertices.push([r * Math.cos(a), r * Math.sin(a)]);
  }

  const centerX = 0;
  const centerY = 0;

  let minX = centerX,
    maxX = centerX,
    minY = centerY,
    maxY = centerY;
  for (const [x, y] of arcVertices) {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }
  const uvWidth = maxX - minX || 1;
  const uvHeight = maxY - minY || 1;

  const getUV = (x: number, y: number): [number, number] => {
    const u = (x - minX) / uvWidth;
    const v = (y - minY) / uvHeight;
    return [1 - u, 1 - v];
  };

  // Front face
  positions.push(centerX, centerY, frontZ);
  const centerUV = getUV(centerX, centerY);
  uvs.push(...centerUV);
  normals.push(0, 0, 1);

  for (let i = 0; i <= segments; i++) {
    const [x, y] = arcVertices[i];
    positions.push(x, y, frontZ);
    const uv = getUV(x, y);
    uvs.push(...uv);
    normals.push(0, 0, 1);
  }

  for (let i = 0; i < segments; i++) {
    indices.push(0, i + 2, i + 1);
  }

  // Back face
  const backOffset = segments + 2;
  positions.push(centerX, centerY, backZ);
  uvs.push(...centerUV);
  normals.push(0, 0, -1);

  for (let i = 0; i <= segments; i++) {
    const [x, y] = arcVertices[i];
    positions.push(x, y, backZ);
    const uv = getUV(x, y);
    uvs.push(...uv);
    normals.push(0, 0, -1);
  }

  for (let i = 0; i < segments; i++) {
    indices.push(backOffset, backOffset + i + 1, backOffset + i + 2);
  }

  // Curved outer edge
  let sideOffset = positions.length / 3;
  for (let i = 0; i < segments; i++) {
    const [x1, y1] = arcVertices[i];
    const [x2, y2] = arcVertices[i + 1];
    const nx1 = x1 / r,
      ny1 = y1 / r;
    const nx2 = x2 / r,
      ny2 = y2 / r;

    positions.push(x1, y1, frontZ);
    positions.push(x1, y1, backZ);
    positions.push(x2, y2, backZ);
    positions.push(x2, y2, frontZ);

    normals.push(nx1, ny1, 0, nx1, ny1, 0, nx2, ny2, 0, nx2, ny2, 0);
    uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);

    indices.push(sideOffset, sideOffset + 1, sideOffset + 2);
    indices.push(sideOffset, sideOffset + 2, sideOffset + 3);
    sideOffset += 4;
  }

  // Straight edges (two radial edges from center to arc endpoints)
  const straightEdges: Array<[[number, number], [number, number]]> = [
    [[centerX, centerY], arcVertices[0]],
    [arcVertices[segments], [centerX, centerY]],
  ];

  for (const [[ax, ay], [bx, by]] of straightEdges) {
    const dx = bx - ax,
      dy = by - ay;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = dy / len,
      ny = -dx / len;

    positions.push(
      ax,
      ay,
      frontZ,
      ax,
      ay,
      backZ,
      bx,
      by,
      backZ,
      bx,
      by,
      frontZ,
    );
    normals.push(nx, ny, 0, nx, ny, 0, nx, ny, 0, nx, ny, 0);
    uvs.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);

    indices.push(sideOffset, sideOffset + 1, sideOffset + 2);
    indices.push(sideOffset, sideOffset + 2, sideOffset + 3);
    sideOffset += 4;
  }

  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geometry.setIndex(indices);

  return geometry;
};
